import { useEffect, useState } from "react";
import { File as FileIcon, Loader2, Download, X } from "lucide-react";
import { api, downloadFile } from "@/lib/api";
import { cn } from "@/lib/utils";

// Shared attachment UI — used by the team messaging composer (Messages.jsx)
// and the support ticket threads (Support.jsx / MyTickets.jsx / SupportDialog.jsx),
// so a file behaves the same way no matter which conversation it's attached to.

export function bytesToSize(n) {
  if (!n) return "0 KB";
  const kb = n / 1024;
  if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * A photo renders as a tappable thumbnail instead of a bare download chip —
 * fetched as a blob through the api client (not a plain <img src>) so the
 * request still carries the session cookie and, for an owner viewing
 * another tenant's data, the X-Tenant-Id header, neither of which a bare
 * <img> tag would send.
 */
export function AttachmentImage({ att, onOpen }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objUrl = null;
    api
      .get(att.download_path, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [att.download_path]);

  if (failed) {
    // Falls back to a plain download chip so a broken fetch still lets the
    // file through rather than showing a dead thumbnail.
    return (
      <a
        href={att.download_path}
        className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[12px] text-ink hover:border-brand-ring transition-colors max-w-full shadow-card"
        title={`Download ${att.filename}`}
      >
        <FileIcon className="w-3.5 h-3.5 text-muted2 shrink-0" />
        <span className="truncate max-w-[180px]">{att.filename}</span>
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => url && onOpen(url, att.filename)}
      className="block w-[200px] h-[160px] rounded-xl overflow-hidden border border-line shadow-card bg-muted"
      title={att.filename}
    >
      {url ? (
        <img src={url} alt={att.filename} className="block w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted2">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}
    </button>
  );
}

function AttachmentChip({ att }) {
  const download = () =>
    downloadFile(att.download_path, att.filename).catch(() => {});
  return (
    <button
      onClick={download}
      className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-[12px] text-ink hover:border-brand-ring transition-colors max-w-full shadow-card"
      title={`Download ${att.filename}`}
    >
      <FileIcon className="w-3.5 h-3.5 text-muted2 shrink-0" />
      <span className="truncate max-w-[180px]">{att.filename}</span>
      <span className="text-muted2 shrink-0">{bytesToSize(att.size)}</span>
      <Download className="w-3 h-3 text-muted2 shrink-0" />
    </button>
  );
}

/** A message/reply's attachments, images as thumbnails and everything else
 * as a download chip, aligned to whichever side that bubble sits on. */
export function AttachmentList({ attachments, align = "start", onOpenImage }) {
  if (!attachments?.length) return null;
  return (
    <div
      className={cn(
        "mt-1.5 space-y-1.5 flex flex-col",
        align === "end" ? "items-end" : "items-start"
      )}
    >
      {attachments.map((att) =>
        att.content_type?.startsWith("image/") ? (
          <AttachmentImage key={att.attachment_id} att={att} onOpen={onOpenImage} />
        ) : (
          <AttachmentChip key={att.attachment_id} att={att} />
        )
      )}
    </div>
  );
}

/** Removable chips for files staged in a composer, before they're sent. */
export function PendingFileChips({ files, onRemove }) {
  if (!files?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {files.map((f, i) => (
        <span
          key={`${f.name}-${i}`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-light text-brand px-2 py-1 text-[11.5px]"
        >
          <FileIcon className="w-3 h-3" />
          <span className="truncate max-w-[140px]">{f.name}</span>
          <button
            onClick={() => onRemove(i)}
            className="hover:text-stock-out"
            aria-label={`Remove ${f.name}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

/** Full-size photo viewer for a thumbnail tapped via AttachmentImage/onOpen. */
export function AttachmentLightbox({ lightbox, onClose }) {
  if (!lightbox) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        title="Close"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <a
        href={lightbox.url}
        download={lightbox.filename}
        onClick={(e) => e.stopPropagation()}
        title={`Download ${lightbox.filename}`}
        className="absolute top-4 left-4 sm:top-6 sm:left-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
      >
        <Download className="w-5 h-5" />
      </a>
      <img
        src={lightbox.url}
        alt={lightbox.filename}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full rounded-lg shadow-2xl object-contain"
      />
    </div>
  );
}
